/**
 * DataTable — the app's table: bordered card, mono uppercase headers,
 * optional pinned first column, optional row groups with their own header
 * bar, and a footer row. Scrolls sideways on narrow screens.
 *
 *   <DataTable
 *     columns={[{ id: "name", header: "Name", cell: (r) => r.name }]}
 *     rows={items}
 *     rowKey={(r) => r.id}
 *   />
 *
 * `verticalDividers` draws a line between every column (header, rows and
 * footer). Group header bars span the full width and stay undivided.
 *
 * Rows get the `group` class, so cells can reveal hover actions with
 * `opacity-0 group-hover:opacity-100`.
 */
import * as React from "react";
import { cn } from "@/lib/utils";

export interface DataTableColumn<T> {
  id: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  align?: "left" | "right" | "center";
  /** CSS width for the column, e.g. "16rem" or "1%" to shrink to content */
  width?: string;
  className?: string;
  headerClassName?: string;
}

export interface DataTableGroup<T> {
  id: string;
  /** Rendered as a full-width bar above the group's rows */
  header: React.ReactNode;
  rows: T[];
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rowKey: (row: T) => string;
  rows?: T[];
  groups?: DataTableGroup<T>[];
  rowClassName?: (row: T) => string | undefined;
  /** Pin the first column while scrolling sideways */
  stickyFirstColumn?: boolean;
  /** Draw a vertical line between columns */
  verticalDividers?: boolean;
  /** Cells for a summary row at the bottom, keyed by column id */
  footer?: Partial<Record<string, React.ReactNode>>;
  hideHeader?: boolean;
  empty?: React.ReactNode;
  /** Below this width the table scrolls sideways instead of squeezing */
  minWidth?: number;
  className?: string;
}

const ALIGN = { left: "text-left", right: "text-right", center: "text-center" } as const;

export function DataTable<T>({
  columns, rowKey, rows, groups, rowClassName, stickyFirstColumn, verticalDividers, footer, hideHeader, empty, minWidth, className,
}: DataTableProps<T>) {
  const sections: DataTableGroup<T>[] = groups ?? [{ id: "_all", header: null, rows: rows ?? [] }];
  const isEmpty = sections.every((g) => g.rows.length === 0);
  const sticky = (i: number) => stickyFirstColumn && i === 0 && "sticky left-0 z-10 bg-card";
  // Every column but the first gets a left border when dividers are on
  const divider = (i: number) => verticalDividers && i > 0 && "border-l border-border";

  return (
    <div className={cn("overflow-x-auto rounded-lg border border-border bg-card", className)}>
      <table className="w-full border-collapse text-left" style={minWidth ? { minWidth } : undefined}>
        <colgroup>
          {columns.map((c) => <col key={c.id} style={c.width ? { width: c.width } : undefined} />)}
        </colgroup>

        {!hideHeader && (
          <thead>
            <tr className="border-b border-border">
              {columns.map((c, i) => (
                <th
                  key={c.id}
                  scope="col"
                  className={cn(
                    "px-4 py-2.5 font-mono text-[11px] font-normal uppercase tracking-widest text-muted-foreground",
                    ALIGN[c.align ?? "left"],
                    sticky(i),
                    divider(i),
                    c.headerClassName,
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
        )}

        {isEmpty ? (
          <tbody>
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center font-sans text-xs text-muted-foreground">
                {empty ?? "Nothing here yet."}
              </td>
            </tr>
          </tbody>
        ) : (
          sections.map((g) => (
            <tbody key={g.id} className="border-b border-border last:border-b-0">
              {g.header != null && g.rows.length > 0 && (
                <tr>
                  <td colSpan={columns.length} className="bg-muted/20 px-4 py-2">
                    {g.header}
                  </td>
                </tr>
              )}
              {g.rows.map((row) => (
                <tr key={rowKey(row)} className={cn("group border-t border-border/60 first:border-t-0 transition-colors hover:bg-foreground/[0.02]", rowClassName?.(row))}>
                  {columns.map((c, i) => (
                    <td key={c.id} className={cn("px-4 py-3 align-middle", ALIGN[c.align ?? "left"], sticky(i), divider(i), c.className)}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))
        )}

        {footer && !isEmpty && (
          <tfoot>
            <tr className="border-t border-border bg-muted/20">
              {columns.map((c, i) => (
                <td
                  key={c.id}
                  className={cn("px-4 py-2.5 font-mono text-xs tabular-nums text-muted-foreground", ALIGN[c.align ?? "left"], sticky(i), divider(i))}
                >
                  {footer[c.id] ?? null}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
